import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import WorkIcon from '@material-ui/icons/Work';

const useStyles = makeStyles({
  root: {
    marginTop: 16,
  },
});

const emptyDraft = (silaeId) => {
  return {
    silae_id: silaeId,
    employer_name: '',
    weekly_hours: null,
    end_date: null,
    active: true,
  };
};

// cleaner_external_employment (plan 013 B2): 1:N per silae_id. The
// "en contrat avec un autre employeur" flag is derived from the presence of at
// least one row with active = true.
const ExternalEmploymentCard = ({ silaeId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft(silaeId));
  const [saving, setSaving] = useState(false);

  const reload = () => {
    if (!silaeId) {
      setRows([]);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    dataProvider
      .getList('cleaner_external_employment', {
        sort: { field: 'active', order: 'DESC' },
        pagination: { page: 1, perPage: 100 },
        filter: { silae_id: silaeId },
      })
      .then(({ data }) => {
        if (!cancelled) setRows(data || []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(reload, [silaeId, dataProvider]);

  const hasActive = rows.some((r) => {
    return r.active;
  });

  const setField = (source, value) => {
    setDraft((prev) => {
      return { ...prev, [source]: value };
    });
  };

  const handleSave = async () => {
    if (!silaeId || !draft.employer_name) return;
    setSaving(true);
    try {
      await dataProvider.create('cleaner_external_employment', {
        data: { ...draft, silae_id: silaeId },
      });
      notify('Employeur ajouté', 'info');
      setOpen(false);
      setDraft(emptyDraft(silaeId));
      reload();
    } catch (err) {
      notify(err.message || "Échec de l'ajout", 'warning');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await dataProvider.delete('cleaner_external_employment', {
        id: row.id,
        previousData: row,
      });
      notify('Employeur retiré', 'info');
      reload();
    } catch (err) {
      notify(err.message || 'Échec de la suppression', 'warning');
    }
  };

  return (
    <Grid item xs={12} className={classes.root}>
      <Card>
        <CardHeader
          avatar={<WorkIcon color="action" />}
          title="Emploi chez un autre employeur"
          subheader={
            <Chip
              size="small"
              color={hasActive ? 'secondary' : 'default'}
              label={
                hasActive
                  ? 'En contrat avec un autre employeur'
                  : 'Pas de contrat externe actif'
              }
            />
          }
          action={
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                return setOpen(true);
              }}
              disabled={!loaded || !silaeId}
            >
              Ajouter
            </Button>
          }
        />
        <CardContent>
          {!silaeId && (
            <Typography variant="body2" color="textSecondary">
              Aucun matricule (silae_id) — employeurs externes indisponibles.
            </Typography>
          )}
          {silaeId && loaded && rows.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Aucun employeur externe renseigné.
            </Typography>
          )}
          {rows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employeur</TableCell>
                  <TableCell align="right">Heures/semaine</TableCell>
                  <TableCell>Date de fin</TableCell>
                  <TableCell>Contrat actif</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.employer_name}</TableCell>
                      <TableCell align="right">
                        {row.weekly_hours == null ? '—' : row.weekly_hours}
                      </TableCell>
                      <TableCell>{row.end_date || '—'}</TableCell>
                      <TableCell>{row.active ? '✅' : '❌'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => {
                            return handleDelete(row);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => {
          return setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ajouter un employeur externe</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nom de l'employeur"
                fullWidth
                value={draft.employer_name}
                onChange={(e) => {
                  return setField('employer_name', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Heures/sem"
                type="number"
                fullWidth
                value={draft.weekly_hours == null ? '' : draft.weekly_hours}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setField('weekly_hours', null);
                    return;
                  }
                  const n = Number(raw);
                  setField('weekly_hours', Number.isNaN(n) ? null : n);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date de fin"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={draft.end_date || ''}
                onChange={(e) => {
                  return setField('end_date', e.target.value || null);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!draft.active}
                    onChange={(e) => {
                      return setField('active', e.target.checked);
                    }}
                  />
                }
                label="Contrat actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              return setOpen(false);
            }}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !draft.employer_name}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

ExternalEmploymentCard.propTypes = {
  silaeId: PropTypes.string,
};

ExternalEmploymentCard.defaultProps = {
  silaeId: '',
};

export default ExternalEmploymentCard;
